import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Candidate } from '../types/hr.types';
import { useToast } from './ui/Toast';
import { Search, Calendar as CalendarIcon, User, ChevronRight, ChevronLeft } from 'lucide-react';
interface CompanyDirectoryModalProps {
  candidate: Candidate | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (candidate: Candidate, nextStatus: Candidate['status']) => void;
  nextStatus?: Candidate['status'];
}
export function CompanyDirectoryModal({
  candidate,
  isOpen,
  onClose,
  onSave,
  nextStatus
}: CompanyDirectoryModalProps) {
  const {
    addToast
  } = useToast();
  // Form State
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [noMiddleName, setNoMiddleName] = useState(false);
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [teamLead, setTeamLead] = useState('');
  const [bsaDate, setBsaDate] = useState('');
  const [user, setUser] = useState('');
  const [methodOfHire, setMethodOfHire] = useState('');
  // Employee Info State
  const [personalPhone, setPersonalPhone] = useState('');
  const [workPhone, setWorkPhone] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [outlookEmail, setOutlookEmail] = useState('');
  const [adpRegistered, setAdpRegistered] = useState('');
  const [dotCard, setDotCard] = useState(false);
  const [dotCardUsername, setDotCardUsername] = useState('');
  const [dotCardPassword, setDotCardPassword] = useState('');
  const [active, setActive] = useState(true);
  const [reasonForSeparation, setReasonForSeparation] = useState('');
  const [notes, setNotes] = useState('');
  const [shirtSize, setShirtSize] = useState('');
  const [rehired, setRehired] = useState(false);
  // Right Column State
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [prevBasisPoints, setPrevBasisPoints] = useState('');
  const [salary, setSalary] = useState('');
  const [prevSalary, setPrevSalary] = useState('');
  const [commission, setCommission] = useState('');
  const [nextPayIncrease, setNextPayIncrease] = useState('');
  const [outlookPassword, setOutlookPassword] = useState('');
  const [windowsPassword, setWindowsPassword] = useState('');
  const [officeCode, setOfficeCode] = useState('');
  const [phoneExt, setPhoneExt] = useState('');
  const [healthInsurance, setHealthInsurance] = useState(false);
  const [healthInsuranceDate, setHealthInsuranceDate] = useState('');
  const [healthEquity, setHealthEquity] = useState(false);
  const [gymMembership, setGymMembership] = useState(false);
  const [lastDayDate, setLastDayDate] = useState('');
  const [lastDayTime, setLastDayTime] = useState('');
  const [dateTerminated, setDateTerminated] = useState('');
  // Dual Listbox State
  const [availablePositions, setAvailablePositions] = useState(['Loan Partner', 'Loan Officer', 'Team Lead', 'Developmental Team Lead', 'Branch Manager']);
  const [chosenPositions, setChosenPositions] = useState<string[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
  const [selectedChosen, setSelectedChosen] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (isOpen && candidate) {
      setFirstName(candidate.firstName || '');
      setLastName(candidate.lastName || '');
      setPersonalPhone(candidate.phone || '');
      setPersonalEmail(candidate.email || '');
      if (candidate.desiredStartDate) {
        const d = new Date(candidate.desiredStartDate);
        setStartDate(d.toISOString().split('T')[0]);
      } else {
        setStartDate('');
      }
      if (candidate.role) {
        setChosenPositions([candidate.role]);
        setAvailablePositions(prev => prev.filter(p => p !== candidate.role));
      }
      // Reset other fields
      setMiddleName('');
      setNoMiddleName(false);
      setSuffix('');
      setTeamLead('');
      setBsaDate('');
      setUser('');
      setMethodOfHire('');
      setWorkPhone('');
      setOutlookEmail('');
      setAdpRegistered('');
      setDotCard(false);
      setDotCardUsername('');
      setDotCardPassword('');
      setActive(true);
      setReasonForSeparation('');
      setNotes(candidate.notes || '');
      setShirtSize('');
      setRehired(false);
      setStartTime('');
      setPrevBasisPoints('');
      setSalary(candidate.offerAmount ? candidate.offerAmount.toString() : '');
      setPrevSalary('');
      setCommission('');
      setNextPayIncrease('');
      setOutlookPassword('');
      setWindowsPassword('');
      setOfficeCode('');
      setPhoneExt('');
      setHealthInsurance(false);
      setHealthInsuranceDate('');
      setHealthEquity(false);
      setGymMembership(false);
      setLastDayDate('');
      setLastDayTime('');
      setDateTerminated('');
      setErrors({});
    }
  }, [isOpen, candidate]);
  if (!candidate) return null;
  const handleMoveRight = () => {
    if (selectedAvailable.length === 0) return;
    setChosenPositions(prev => [...prev, ...selectedAvailable]);
    setAvailablePositions(prev => prev.filter(p => !selectedAvailable.includes(p)));
    setSelectedAvailable([]);
  };
  const handleMoveLeft = () => {
    if (selectedChosen.length === 0) return;
    setAvailablePositions(prev => [...prev, ...selectedChosen]);
    setChosenPositions(prev => prev.filter(p => !selectedChosen.includes(p)));
    setSelectedChosen([]);
  };
  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = 'Complete this field.';
    if (!lastName.trim()) newErrors.lastName = 'Complete this field.';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to top to show errors
      const modalBody = document.getElementById('directory-modal-body');
      if (modalBody) modalBody.scrollTop = 0;
      return;
    }
    if (nextStatus) {
      onSave(candidate, nextStatus);
      addToast({
        type: 'success',
        title: 'Directory Profile Created',
        message: `${firstName} ${lastName} has been added to the company directory.`
      });
    }
    onClose();
  };
  const inputBase = 'w-full px-2 py-1.5 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[13px] bg-white';
  const labelBase = 'block text-[12px] text-gray-700 mb-1';
  const sectionHeader = 'text-[14px] font-semibold text-gray-800 bg-gray-100 px-3 py-2 rounded-t border-b border-gray-200 mb-4';
  return <Modal isOpen={isOpen} onClose={onClose} title="Create Company Directory Profile" maxWidth="5xl" data-id="element-556">
      <div className="flex flex-col h-[80vh]" data-id="element-557">
        <div className="text-right text-[11px] text-red-600 mb-2 px-4" data-id="element-558">
          * = Required Information
        </div>

        <div id="directory-modal-body" className="flex-1 overflow-y-auto px-4 pb-6 space-y-6 custom-scrollbar" data-id="element-559">
          {/* Information Section */}
          <div className="border border-gray-200 rounded" data-id="element-560">
            <div className={sectionHeader} data-id="element-561">Information</div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4" data-id="element-562">
              {/* Left Col */}
              <div className="space-y-3" data-id="element-563">
                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-564">
                  <label className={labelBase} data-id="element-565">
                    <span className="text-red-500" data-id="element-566">*</span>First Name
                  </label>
                  <div data-id="element-567">
                    <input type="text" value={firstName} onChange={e => {
                    setFirstName(e.target.value);
                    if (errors.firstName) setErrors({
                      ...errors,
                      firstName: ''
                    });
                  }} className={`${inputBase} ${errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} data-id="element-568" />
                    {errors.firstName && <p className="text-[10px] text-red-500 mt-0.5" data-id="element-569">
                        {errors.firstName}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-570">
                  <label className={labelBase} data-id="element-571">Middle Name</label>
                  <input type="text" value={middleName} onChange={e => setMiddleName(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-572" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-573">
                  <label className={labelBase} data-id="element-574">No Middle Name</label>
                  <input type="checkbox" checked={noMiddleName} onChange={e => setNoMiddleName(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-575" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-576">
                  <label className={labelBase} data-id="element-577">
                    <span className="text-red-500" data-id="element-578">*</span>Last Name
                  </label>
                  <div data-id="element-579">
                    <input type="text" value={lastName} onChange={e => {
                    setLastName(e.target.value);
                    if (errors.lastName) setErrors({
                      ...errors,
                      lastName: ''
                    });
                  }} className={`${inputBase} ${errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} data-id="element-580" />
                    {errors.lastName && <p className="text-[10px] text-red-500 mt-0.5" data-id="element-581">
                        {errors.lastName}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-582">
                  <label className={labelBase} data-id="element-583">Suffix</label>
                  <select value={suffix} onChange={e => setSuffix(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-584">
                    <option value="" data-id="element-585">--None--</option>
                    <option value="Jr." data-id="element-586">Jr.</option>
                    <option value="Sr." data-id="element-587">Sr.</option>
                    <option value="II" data-id="element-588">II</option>
                    <option value="III" data-id="element-589">III</option>
                  </select>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-590">
                  <label className={labelBase} data-id="element-591">Birthdate</label>
                  <span className="text-[11px] text-gray-500 italic" data-id="element-592">
                    This field is calculated upon save
                  </span>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-593">
                  <label className={labelBase} data-id="element-594">
                    Employee
                    <br data-id="element-595" />
                    Anniversary
                  </label>
                  <span className="text-[11px] text-gray-500 italic" data-id="element-596">
                    This field is calculated upon save
                  </span>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-597">
                  <label className={labelBase} data-id="element-598">Team Lead</label>
                  <div className="relative" data-id="element-599">
                    <input type="text" value={teamLead} onChange={e => setTeamLead(e.target.value)} placeholder="Search People..." className={`${inputBase} border-gray-300 pr-8`} data-id="element-600" />
                    <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" data-id="element-601" />
                  </div>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-602">
                  <label className={labelBase} data-id="element-603">
                    Start date + 37
                    <br data-id="element-604" />
                    Months
                  </label>
                  <span className="text-[11px] text-gray-500 italic" data-id="element-605">
                    This field is calculated upon save
                  </span>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-606">
                  <label className={labelBase} data-id="element-607">
                    Date BSA/AML
                    <br data-id="element-608" />
                    Training Completed
                  </label>
                  <div className="relative" data-id="element-609">
                    <input type="date" value={bsaDate} onChange={e => setBsaDate(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-610" />
                  </div>
                </div>
              </div>

              {/* Right Col */}
              <div className="space-y-3" data-id="element-611">
                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-612">
                  <label className={labelBase} data-id="element-613">Owner</label>
                  <div className="flex items-center gap-2 text-[13px] text-gray-800" data-id="element-614">
                    <User className="h-4 w-4 text-gray-500" data-id="element-615" />
                    Stephen Western
                  </div>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-616">
                  <label className={labelBase} data-id="element-617">User</label>
                  <div className="relative" data-id="element-618">
                    <input type="text" value={user} onChange={e => setUser(e.target.value)} placeholder="Search People..." className={`${inputBase} border-gray-300 pr-8`} data-id="element-619" />
                    <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" data-id="element-620" />
                  </div>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-621">
                  <label className={labelBase} data-id="element-622">Method of Hire</label>
                  <select value={methodOfHire} onChange={e => setMethodOfHire(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-623">
                    <option value="" data-id="element-624">--None--</option>
                    <option value="Direct" data-id="element-625">Direct</option>
                    <option value="Agency" data-id="element-626">Agency</option>
                    <option value="Referral" data-id="element-627">Referral</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Information Section */}
          <div className="border border-gray-200 rounded" data-id="element-628">
            <div className={sectionHeader} data-id="element-629">Employee Information</div>
            <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-6" data-id="element-630">
              {/* Left Col */}
              <div className="space-y-4" data-id="element-631">
                {/* Dual Listbox for Position */}
                <div className="grid grid-cols-[120px_1fr]" data-id="element-632">
                  <label className={labelBase} data-id="element-633">Position</label>
                  <div className="flex items-stretch gap-2 h-40" data-id="element-634">
                    <div className="flex-1 flex flex-col" data-id="element-635">
                      <span className="text-[11px] text-gray-500 mb-1" data-id="element-636">
                        Available
                      </span>
                      <select multiple value={selectedAvailable} onChange={e => setSelectedAvailable(Array.from(e.target.selectedOptions, option => option.value))} className="flex-1 border border-gray-300 rounded text-[12px] p-1 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-637">
                        {availablePositions.map(p => <option key={p} value={p} data-id="element-638">
                            {p}
                          </option>)}
                      </select>
                    </div>
                    <div className="flex flex-col justify-center gap-2 px-1" data-id="element-639">
                      <button onClick={handleMoveRight} type="button" className="p-1 hover:bg-gray-100 rounded text-gray-600" data-id="element-640">
                        <ChevronRight className="h-4 w-4" data-id="element-641" />
                      </button>
                      <button onClick={handleMoveLeft} type="button" className="p-1 hover:bg-gray-100 rounded text-gray-600" data-id="element-642">
                        <ChevronLeft className="h-4 w-4" data-id="element-643" />
                      </button>
                    </div>
                    <div className="flex-1 flex flex-col" data-id="element-644">
                      <span className="text-[11px] text-gray-500 mb-1" data-id="element-645">
                        Chosen
                      </span>
                      <select multiple value={selectedChosen} onChange={e => setSelectedChosen(Array.from(e.target.selectedOptions, option => option.value))} className="flex-1 border border-gray-300 rounded text-[12px] p-1 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-646">
                        {chosenPositions.map(p => <option key={p} value={p} data-id="element-647">
                            {p}
                          </option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-648">
                  <label className={labelBase} data-id="element-649">
                    Personal Phone
                    <br data-id="element-650" />
                    Number
                  </label>
                  <input type="text" value={personalPhone} onChange={e => setPersonalPhone(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-651" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-652">
                  <label className={labelBase} data-id="element-653">Work Phone Number</label>
                  <input type="text" value={workPhone} onChange={e => setWorkPhone(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-654" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-655">
                  <label className={labelBase} data-id="element-656">Personal Email</label>
                  <input type="email" value={personalEmail} onChange={e => setPersonalEmail(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-657" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-658">
                  <label className={labelBase} data-id="element-659">Outlook Email</label>
                  <input type="email" value={outlookEmail} onChange={e => setOutlookEmail(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-660" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-661">
                  <label className={labelBase} data-id="element-662">ADP Registered</label>
                  <select value={adpRegistered} onChange={e => setAdpRegistered(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-663">
                    <option value="" data-id="element-664">--None--</option>
                    <option value="Yes" data-id="element-665">Yes</option>
                    <option value="No" data-id="element-666">No</option>
                  </select>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-667">
                  <label className={labelBase} data-id="element-668">Dot Card</label>
                  <input type="checkbox" checked={dotCard} onChange={e => setDotCard(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-669" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-670">
                  <label className={labelBase} data-id="element-671">Dot Card Username</label>
                  <input type="text" value={dotCardUsername} onChange={e => setDotCardUsername(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-672" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-673">
                  <label className={labelBase} data-id="element-674">Dot Card Password</label>
                  <input type="password" value={dotCardPassword} onChange={e => setDotCardPassword(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-675" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-676">
                  <label className={labelBase} data-id="element-677">Active</label>
                  <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-678" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-679">
                  <label className={labelBase} data-id="element-680">Reason for Separation</label>
                  <select value={reasonForSeparation} onChange={e => setReasonForSeparation(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-681">
                    <option value="" data-id="element-682">--None--</option>
                    <option value="Resigned" data-id="element-683">Resigned</option>
                    <option value="Terminated" data-id="element-684">Terminated</option>
                  </select>
                </div>

                <div className="grid grid-cols-[120px_1fr]" data-id="element-685">
                  <label className={labelBase} data-id="element-686">Notes</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className={`${inputBase} border-gray-300 resize-none`} data-id="element-687" />
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-688">
                  <label className={labelBase} data-id="element-689">Shirt Size</label>
                  <select value={shirtSize} onChange={e => setShirtSize(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-690">
                    <option value="" data-id="element-691">--None--</option>
                    <option value="S" data-id="element-692">S</option>
                    <option value="M" data-id="element-693">M</option>
                    <option value="L" data-id="element-694">L</option>
                    <option value="XL" data-id="element-695">XL</option>
                    <option value="XXL" data-id="element-696">XXL</option>
                  </select>
                </div>

                <div className="grid grid-cols-[120px_1fr] items-center" data-id="element-697">
                  <label className={labelBase} data-id="element-698">Rehired</label>
                  <input type="checkbox" checked={rehired} onChange={e => setRehired(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-699" />
                </div>
              </div>

              {/* Right Col */}
              <div className="space-y-4" data-id="element-700">
                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-701">
                  <label className={labelBase} data-id="element-702">Start Date/Time</label>
                  <div className="flex gap-2" data-id="element-703">
                    <div className="flex-1" data-id="element-704">
                      <span className="text-[10px] text-gray-500 block mb-0.5" data-id="element-705">
                        Date
                      </span>
                      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-706" />
                    </div>
                    <div className="flex-1" data-id="element-707">
                      <span className="text-[10px] text-gray-500 block mb-0.5" data-id="element-708">
                        Time
                      </span>
                      <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-709" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-710">
                  <label className={labelBase} data-id="element-711">Previous Basis Points</label>
                  <input type="text" value={prevBasisPoints} onChange={e => setPrevBasisPoints(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-712" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-713">
                  <label className={labelBase} data-id="element-714">Salary</label>
                  <input type="text" value={salary} onChange={e => setSalary(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-715" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-716">
                  <label className={labelBase} data-id="element-717">Previous Salary</label>
                  <input type="text" value={prevSalary} onChange={e => setPrevSalary(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-718" />
                </div>

                <div className="grid grid-cols-[140px_1fr]" data-id="element-719">
                  <label className={labelBase} data-id="element-720">Commission Structure</label>
                  <textarea value={commission} onChange={e => setCommission(e.target.value)} rows={3} className={`${inputBase} border-gray-300 resize-none`} data-id="element-721" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-722">
                  <label className={labelBase} data-id="element-723">
                    Date of Next Pay
                    <br data-id="element-724" />
                    Increase
                  </label>
                  <input type="date" value={nextPayIncrease} onChange={e => setNextPayIncrease(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-725" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-726">
                  <label className={labelBase} data-id="element-727">Outlook Password</label>
                  <input type="password" value={outlookPassword} onChange={e => setOutlookPassword(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-728" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-729">
                  <label className={labelBase} data-id="element-730">Windows Password</label>
                  <input type="password" value={windowsPassword} onChange={e => setWindowsPassword(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-731" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-732">
                  <label className={labelBase} data-id="element-733">Office Code</label>
                  <input type="text" value={officeCode} onChange={e => setOfficeCode(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-734" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-735">
                  <label className={labelBase} data-id="element-736">Phone Extension</label>
                  <input type="text" value={phoneExt} onChange={e => setPhoneExt(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-737" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-738">
                  <label className={labelBase} data-id="element-739">Health Insurance</label>
                  <input type="checkbox" checked={healthInsurance} onChange={e => setHealthInsurance(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-740" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-741">
                  <label className={labelBase} data-id="element-742">
                    Health Insurance Date
                    <br data-id="element-743" />
                    Enrolled
                  </label>
                  <input type="date" value={healthInsuranceDate} onChange={e => setHealthInsuranceDate(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-744" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-745">
                  <label className={labelBase} data-id="element-746">Health Equity Account</label>
                  <input type="checkbox" checked={healthEquity} onChange={e => setHealthEquity(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-747" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-748">
                  <label className={labelBase} data-id="element-749">Gym Membership</label>
                  <input type="checkbox" checked={gymMembership} onChange={e => setGymMembership(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-750" />
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-751">
                  <label className={labelBase} data-id="element-752">Last Day Work</label>
                  <div className="flex gap-2" data-id="element-753">
                    <div className="flex-1" data-id="element-754">
                      <span className="text-[10px] text-gray-500 block mb-0.5" data-id="element-755">
                        Date
                      </span>
                      <input type="date" value={lastDayDate} onChange={e => setLastDayDate(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-756" />
                    </div>
                    <div className="flex-1" data-id="element-757">
                      <span className="text-[10px] text-gray-500 block mb-0.5" data-id="element-758">
                        Time
                      </span>
                      <input type="time" value={lastDayTime} onChange={e => setLastDayTime(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-759" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-[140px_1fr] items-center" data-id="element-760">
                  <label className={labelBase} data-id="element-761">Date Terminated</label>
                  <input type="date" value={dateTerminated} onChange={e => setDateTerminated(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-762" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 rounded-b-lg" data-id="element-763">
          <Button variant="ghost" onClick={onClose} className="text-gray-700 border border-gray-300 hover:bg-gray-100" data-id="element-764">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700" data-id="element-765">
            Save
          </Button>
        </div>
      </div>
    </Modal>;
}